/**
 * MerlinSelect — the single app-owned wrapper over react-select v5.
 *
 * Why this exists
 * ---------------
 * The app reached react-select **v1** two ways: the vendored `reactsymbols-kit`
 * `RSSelect` passthrough, and direct `Creatable` imports. v1 calls
 * `findDOMNode`, removed in React 19, so every dropdown crashed on open. v5 is a
 * near-total API rewrite. This wrapper is the ONE place the v1→v5 prop +
 * styling translation lives, so the ~12 call sites keep their OLD (v1)
 * call-site semantics and change as little as possible.
 *
 * Styling parity
 * --------------
 * The app imports ZERO react-select CSS of its own. Every select is styled by
 * the v1 `.Select-control` / `.Select-menu` / `.Select-option` / … class tree in
 * `reactsymbols-kit/ReactSymbolsKit.css`, plus app overrides that target those
 * same classes (`diagram/fsmFlow.scss`, `Inspector/edit_mpc_styles.scss`). v5
 * emits emotion `.css-*` classes on DIFFERENT DOM, so a naive swap renders but
 * looks wrong.
 *
 * The chosen strategy keeps that proven CSS authoritative: run v5 `unstyled`
 * (drop emotion's defaults) and re-emit the v1 `.Select-*` class names onto the
 * matching v5 elements via the `classNames` API. The existing stylesheets then
 * apply verbatim — byte-identical look without re-deriving every rule. A small
 * `styles` patch only neutralises the emotion wrappers v5 still injects (the
 * menu portal-less container, indicator separators) that have no v1 analogue.
 *
 * v1 → v5 prop mapping (normalised here, NOT at the call sites)
 * ------------------------------------------------------------
 * - `value`: v1 = bare scalar (id/string). v5 = the full option object. We
 *   resolve scalar → option via `options.find` (honouring `valueKey`). Get this
 *   right or the control renders empty.
 * - `onChange`: v1 = `(option | null)`; call sites variously read `{value}`,
 *   take the whole option (extra keys like `dependency`/`method`), or handle
 *   `null`. We forward the selected option object (and `null` on clear),
 *   preserving v1 semantics.
 * - `searchable`→`isSearchable`, `clearable`→`isClearable`,
 *   `disabled`→`isDisabled`, `multi`→`isMulti`.
 * - `deleteRemoves` / `backspaceRemoves={false}`: no v5 equivalent; with
 *   `isClearable={false}` that behaviour is already gone, so they are dropped.
 * - `valueKey='id'` → `getOptionValue`.
 * - `optionRenderer` / `valueRenderer` → v5 `components={{ Option, SingleValue }}`.
 *
 * Classic JSX runtime (`jsxRuntime: 'classic'`) needs React in scope.
 */
import React from 'react';
import ReactSelect, {
  components as RSComponents,
  type ClassNamesConfig,
  type GroupBase,
  type OptionProps,
  type SingleValueProps,
  type StylesConfig,
  type Props as ReactSelectProps,
} from 'react-select';
import CreatableSelect from 'react-select/creatable';

/**
 * The option shape. v1 options were plain `{ label, value }` objects, often
 * carrying extra keys the `onChange` handler reads back (`dependency`,
 * `method`, …) or an alternative value key (`id`). Kept permissive on purpose.
 *
 * Note: no string index signature — call sites pass their own concrete option
 * interfaces (`{ label; value }`, path objects, …), and TS only accepts a typed
 * object as `Record<string, unknown>` if it has an index signature. The wrapper
 * therefore treats options structurally and reads arbitrary keys via the
 * `optionScalar` / renderer boundaries, which cast as needed.
 */
export interface MerlinSelectOption {
  label?: React.ReactNode;
  value?: unknown;
}

type AnyOption = MerlinSelectOption;

export interface MerlinSelectProps {
  /** Forwarded to the outer wrapper div (the v1 `RSSelect` div passthrough). */
  className?: string;
  /** The form field name. */
  name?: string;
  /** The selectable options. */
  options?: AnyOption[];
  /**
   * The current value as a bare scalar (v1 semantics). Resolved to the matching
   * option via `options` + `valueKey`. `null` / `undefined` / `''` render the
   * placeholder.
   */
  value?: unknown;
  /**
   * Change handler. Receives the full selected option object, or `null` when
   * the value is cleared — exactly the v1 contract.
   */
  onChange?: (option: AnyOption | null) => void;
  /** Placeholder text/node. */
  placeholder?: React.ReactNode;
  /** v1 `searchable` → v5 `isSearchable`. Defaults to react-select's default. */
  searchable?: boolean;
  /** v1 `clearable` → v5 `isClearable`. */
  clearable?: boolean;
  /** v1 `disabled` → v5 `isDisabled`. */
  disabled?: boolean;
  /** v1 `multi` → v5 `isMulti`. */
  multi?: boolean;
  /** v1 `valueKey` → v5 `getOptionValue` (the key holding the scalar value). */
  valueKey?: string;
  /** Enable creatable mode (`react-select/creatable`). */
  creatable?: boolean;
  /** v1 `promptTextCreator` → v5 `formatCreateLabel`. */
  promptTextCreator?: (label: string) => string;
  /** v1 `optionRenderer` → v5 `components.Option` body. */
  optionRenderer?: (option: AnyOption) => React.ReactNode;
  /** v1 `valueRenderer` → v5 `components.SingleValue` body. */
  valueRenderer?: (option: AnyOption) => React.ReactNode;
  /**
   * Accepted and ignored for styling/markup parity (the v1 kit forwarded
   * unknown props to the underlying control; `label` was harmless there).
   */
  label?: string;
  /** Accepted for parity; the v1 kit forwarded these without effect here. */
  deleteRemoves?: boolean;
  backspaceRemoves?: boolean;
}

/**
 * Read a scalar value out of an option, honouring `valueKey` (v1 `valueKey`
 * defaulted to `'value'`).
 */
function optionScalar(option: AnyOption, valueKey: string): unknown {
  return (option as Record<string, unknown>)[valueKey];
}

/**
 * Re-emit the v1 `.Select-*` class names onto the v5 elements so the existing
 * stylesheets (kit + app overrides) apply verbatim. Returns the class string(s)
 * react-select concatenates onto each sub-component.
 *
 * The class set mirrors v1's DOM so every selector in
 * `ReactSymbolsKit.css` / `fsmFlow.scss` / `edit_mpc_styles.scss` still matches:
 * container modifiers (`Select--single`/`--multi`, `is-open`, `is-focused`,
 * `is-disabled`, `is-searchable`, `has-value`), the control, value, placeholder,
 * arrow zone/arrow, menu outer/inner, and options (`is-focused`/`is-selected`).
 */
const merlinClassNames: ClassNamesConfig<AnyOption, boolean, GroupBase<AnyOption>> = {
  container: (state) => {
    const classes = ['Select'];
    classes.push(state.isMulti ? 'Select--multi' : 'Select--single');
    if (state.isDisabled) {
      classes.push('is-disabled');
    }
    if (state.selectProps.isSearchable) {
      classes.push('is-searchable');
    }
    if (state.selectProps.menuIsOpen) {
      classes.push('is-open');
    }
    // `hasValue` is exposed on the container state in v5.
    if (state.hasValue) {
      classes.push('has-value');
    }
    if (state.selectProps.isClearable) {
      classes.push('is-clearable');
    }
    return classes.join(' ');
  },
  control: (state) => {
    const classes = ['Select-control'];
    if (state.isFocused) {
      classes.push('is-focused');
    }
    return classes.join(' ');
  },
  valueContainer: () => 'Select-multi-value-wrapper',
  singleValue: () => 'Select-value',
  placeholder: () => 'Select-placeholder',
  input: () => 'Select-input',
  indicatorsContainer: () => 'Select-arrow-zone',
  dropdownIndicator: () => 'Select-arrow',
  clearIndicator: () => 'Select-clear-zone',
  menu: () => 'Select-menu-outer',
  menuList: () => 'Select-menu',
  noOptionsMessage: () => 'Select-noresults',
  option: (state) => {
    const classes = ['Select-option'];
    if (state.isSelected) {
      classes.push('is-selected');
    }
    if (state.isFocused) {
      classes.push('is-focused');
    }
    if (state.isDisabled) {
      classes.push('is-disabled');
    }
    return classes.join(' ');
  },
  multiValue: () => 'Select-value',
  multiValueLabel: () => 'Select-value-label',
  multiValueRemove: () => 'Select-value-icon',
};

/**
 * In `unstyled` mode react-select skips its emotion defaults, so the existing
 * `.Select-*` CSS owns the look. A few elements have no v1 analogue (the arrow
 * is drawn purely by the `.Select-arrow` border CSS, not an SVG, and v5's
 * indicator separator does not exist in v1): hide those, and let the
 * `.Select-arrow` chevron render via the dropdown-indicator class.
 */
const merlinStyles: StylesConfig<AnyOption, boolean, GroupBase<AnyOption>> = {
  // v5 inserts an extra value-container wrapper between the control and the
  // single value / placeholder that v1 never had. The kit's v1 rule
  // `.Select--single > .Select-control .Select-value` (shared with
  // `.Select-placeholder`) centres the text by absolutely filling the control
  // (`top/bottom:0` + `line-height:34px`). Because v5 makes this wrapper a
  // positioned element, that absolute fill resolves to the wrapper's collapsed
  // (~18px) height instead of the 36px control, so the 34px line overflows and
  // is clipped by `.Select-control { overflow:hidden }` (the bug: the selected
  // value/placeholder appears cut off at the bottom everywhere). Removing the
  // wrapper's positioning lets the value/placeholder resolve against
  // `.Select-control` again — restoring the v1 vertical centring verbatim.
  valueContainer: (base) => ({ ...base, position: 'static' }),
  // v1 had no separator between value and arrow.
  indicatorSeparator: () => ({ display: 'none' }),
  // The v1 arrow is a pure-CSS rotated-square chevron drawn by `.Select-arrow`.
  // v5's dropdown indicator renders an SVG child; blank the indicator's own box
  // (the `.Select-arrow` element keeps the v1 25px arrow-zone geometry) and hide
  // the inner SVG so only the CSS chevron shows.
  dropdownIndicator: () => ({
    display: 'block',
    boxSizing: 'border-box',
    position: 'relative',
    '& svg': { display: 'none' },
  }),
  // Let the existing `.Select-menu-outer` rules fully own the menu box.
  menu: () => ({}),
};

/**
 * Build the `components` override map from v1 `optionRenderer` / `valueRenderer`.
 * v5 passes `{ data, innerProps, ... }`; the v1 renderers took the bare option,
 * so we adapt by forwarding `props.data` while keeping the v5 wrapper element
 * (which carries the re-emitted `.Select-option` / `.Select-value` classes).
 */
function buildComponents(
  optionRenderer?: (option: AnyOption) => React.ReactNode,
  valueRenderer?: (option: AnyOption) => React.ReactNode
): Partial<ReactSelectProps<AnyOption, boolean, GroupBase<AnyOption>>['components']> {
  const overrides: Record<string, unknown> = {};
  if (optionRenderer != null) {
    overrides.Option = (props: OptionProps<AnyOption, boolean, GroupBase<AnyOption>>) => (
      <RSComponents.Option {...props}>{optionRenderer(props.data)}</RSComponents.Option>
    );
  }
  if (valueRenderer != null) {
    overrides.SingleValue = (
      props: SingleValueProps<AnyOption, boolean, GroupBase<AnyOption>>
    ) => (
      <RSComponents.SingleValue {...props}>{valueRenderer(props.data)}</RSComponents.SingleValue>
    );
  }
  return overrides;
}

/**
 * The app-owned select. Renders react-select v5 (or its creatable variant)
 * wrapped in the same `<div className={className}>` the v1 `RSSelect` used, so
 * call sites that styled that wrapper (e.g. the FSM bar's `.rs-input-wrap`) are
 * unaffected.
 */
export default function MerlinSelect({
  className,
  name,
  options = [],
  value,
  onChange,
  placeholder,
  searchable,
  clearable,
  disabled,
  multi,
  valueKey = 'value',
  creatable,
  promptTextCreator,
  optionRenderer,
  valueRenderer,
  // `label`, `deleteRemoves`, `backspaceRemoves` are accepted for parity and
  // intentionally not forwarded (no v5 effect).
  label: _label,
  deleteRemoves: _deleteRemoves,
  backspaceRemoves: _backspaceRemoves,
}: MerlinSelectProps) {
  // Resolve the v1 scalar `value` to the v5 option object.
  const isEmptyValue = value === null || value === undefined || value === '';
  const selectedOption = isEmptyValue
    ? null
    : options.find((o) => optionScalar(o, valueKey) === value) ?? null;

  const handleChange = (next: AnyOption | null): void => {
    if (onChange != null) {
      // v5 passes the option (or null on clear); forward the v1 contract.
      onChange(next ?? null);
    }
  };

  const components = buildComponents(optionRenderer, valueRenderer);

  const sharedProps: ReactSelectProps<AnyOption, boolean, GroupBase<AnyOption>> = {
    name,
    options,
    value: selectedOption,
    onChange: (next) => handleChange((next as AnyOption | null) ?? null),
    placeholder: placeholder ?? 'Select...',
    isSearchable: searchable,
    isClearable: clearable,
    isDisabled: disabled,
    isMulti: multi,
    getOptionValue: (o) => String(optionScalar(o, valueKey)),
    getOptionLabel: (o) => (o.label != null ? String(o.label) : ''),
    unstyled: true,
    classNames: merlinClassNames,
    styles: merlinStyles,
    components,
    // v5 portals the menu to the body by default, which breaks the app's
    // `.Select-menu-outer` absolute positioning relative to `.Select`. Render
    // the menu inline (v1 behaviour) so the existing CSS positions it.
    menuPortalTarget: null,
    // The v1 menu is a fixed 200px white box (`.Select-menu-outer`) whose inner
    // list (`.Select-menu`) scrolls at 198px. v5 instead caps the scroll list at
    // its own `maxMenuHeight` default of 300px, overriding the kit's 198px. Since
    // the white background lives only on the 200px outer (which has
    // `overflow:visible`), the extra ~100px of options spill out below it on a
    // TRANSPARENT background — so whatever sits behind the menu (e.g. the diagram
    // canvas and its arrows) bleeds through the bottom rows. Pin the height to the
    // v1 198px so the list scrolls inside the white box again.
    maxMenuHeight: 198,
  };

  const inner = creatable ? (
    <CreatableSelect
      {...sharedProps}
      formatCreateLabel={
        promptTextCreator != null
          ? (inputValue: string) => promptTextCreator(inputValue)
          : undefined
      }
    />
  ) : (
    <ReactSelect {...sharedProps} />
  );

  return <div className={className}>{inner}</div>;
}
